// SPDX-License-Identifier: AGPL-3.0-only
import { useEffect, useState } from 'react';
import { Alert, Anchor, Card, Group, Loader, Stack, Text, Title } from '@mantine/core';
import { Link, useParams } from 'react-router-dom';

import { ApiError, api } from '../api/client';
import type { GatewaySummary } from '../api/client';
import { GatewayStatusCard } from '../components/observability/GatewayStatusCard';
import { GatewayLogs } from '../components/observability/GatewayLogs';

/**
 * The Gateways list.
 *
 * # WHY THE LIST DOES NOT SHOW A STATUS BADGE PER ROW
 *
 * It could — but each badge would be its own status request, and a list of
 * fifty Gateways would poll fifty times every ten seconds to render fifty
 * coloured dots. The list shows what the Gateway IS; the detail page shows what
 * it is DOING, and that is where the live question is worth asking.
 *
 * A bulk status endpoint would change this, and is not built: nobody has a
 * fifty-Gateway installation yet, and an endpoint designed for a scale nobody
 * has is an endpoint designed from a guess.
 */
export function Gateways() {
  const [gateways, setGateways] = useState<GatewaySummary[] | null>(null);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const result = await api.gateways();
        setGateways(result.items);
      } catch (err) {
        setError(err as ApiError);
      }
    })();
  }, []);

  if (error) {
    return (
      <Alert color="red" title="Could not list the Gateways">
        {error.message}
      </Alert>
    );
  }
  if (!gateways) return <Loader size="sm" />;

  return (
    <Stack gap="md">
      <Title order={2}>Gateways</Title>
      {gateways.length === 0 && (
        <Text size="sm" c="dimmed">
          No Gateways are enrolled yet.
        </Text>
      )}
      {gateways.map((gateway) => (
        <Card key={gateway.id} withBorder>
          <Group justify="space-between">
            <Stack gap={2}>
              <Anchor component={Link} to={`/gateways/${gateway.id}`} fw={500}>
                {gateway.name}
              </Anchor>
              <Text size="xs" c="dimmed">
                {gateway.id}
              </Text>
            </Stack>
            <Text size="sm" c={gateway.state === 'revoked' ? 'red' : 'dimmed'}>
              {gateway.state}
            </Text>
          </Group>
        </Card>
      ))}
    </Stack>
  );
}

/**
 * One Gateway: whether it is connected, and what it has been doing.
 *
 * The two halves answer the two questions an operator arrives with — "is it
 * up?" and "what happened?" — and they are deliberately separate components
 * because they come from separate sources: live session state, and a stored
 * event history.
 */
export function GatewayDetail() {
  const { gatewayId } = useParams<{ gatewayId: string }>();
  if (!gatewayId) return null;

  return (
    <Stack gap="lg">
      <Title order={2}>Gateway</Title>
      <GatewayStatusCard gatewayId={gatewayId} />
      <GatewayLogs gatewayId={gatewayId} />
    </Stack>
  );
}
